import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { dropdownPortalPosition, stopModalScrollLockPropagation } from './dropdownPortal';
import DropdownSearchPortalLayer from './DropdownSearchPortalLayer';

/**
 * Multi-select dropdown with search. User types to filter, clicks to add.
 * Selected items are shown as chips below with remove (X).
 *
 * @param {Object[]} options - Array of option objects
 * @param {*[]} selectedIds - Array of selected values (getOptionValue)
 * @param {(ids: *[]) => void} onChange - Called with new selected ids array
 * @param {(option: Object) => *} getOptionValue - Value for comparison (e.g. id)
 * @param {(option: Object) => string} getOptionLabel - Primary label
 * @param {(option: Object) => string} [getOptionSublabel] - Optional secondary line
 * @param {string} [placeholder='Search to add…'] - Input placeholder
 * @param {string} [emptyMessage='No matches'] - When filter has no results
 * @param {boolean} [disabled=false]
 * @param {string} [id]
 * @param {string} [className]
 */
function MultiSelectDropdownSearch({
  options = [],
  selectedIds = [],
  onChange,
  getOptionValue = (o) => o.id,
  getOptionLabel = (o) => o.label,
  getOptionSublabel,
  placeholder = 'Search to add…',
  emptyMessage = 'No matches',
  disabled = false,
  id,
  className = '',
  comboboxAriaLabel,
}: any) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuRect, setMenuRect] = useState<any>(null);
  const wrapperRef = useRef<any>(null);
  const listRef = useRef<any>(null);
  const inputRef = useRef<any>(null);

  const updateMenuRect = useCallback(() => {
    if (!open || !wrapperRef.current) return;
    setMenuRect(dropdownPortalPosition(wrapperRef.current.getBoundingClientRect()));
  }, [open]);

  const selectedSet = new Set(selectedIds);
  const availableOptions = options.filter((o) => !selectedSet.has(getOptionValue(o)));
  const queryLower = (query || '').trim().toLowerCase();
  const filtered = !queryLower.length
    ? availableOptions
    : availableOptions.filter((o) => {
        const label = getOptionLabel(o);
        const sub = getOptionSublabel ? getOptionSublabel(o) : '';
        return (
          (label && label.toLowerCase().includes(queryLower)) ||
          (sub && sub.toLowerCase().includes(queryLower))
        );
      });

  const selectedOptions = selectedIds
    .map((id) => options.find((o) => getOptionValue(o) === id))
    .filter(Boolean);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlightIndex(0);
    } else {
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }
    updateMenuRect();
    window.addEventListener('scroll', updateMenuRect, true);
    window.addEventListener('resize', updateMenuRect);
    return () => {
      window.removeEventListener('scroll', updateMenuRect, true);
      window.removeEventListener('resize', updateMenuRect);
    };
  }, [open, updateMenuRect]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-highlighted="true"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlightIndex, open]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = filtered[highlightIndex];
      if (option) {
        onChange([...selectedIds, getOptionValue(option)]);
        setOpen(false);
      }
      return;
    }
  };

  const handleSelect = (option) => {
    onChange([...selectedIds, getOptionValue(option)]);
    setOpen(false);
  };

  const handleRemove = (value) => {
    onChange(selectedIds.filter((id) => id !== value));
  };

  /**
   * Clicks on the bar toggle the menu (chevron / padding). Clicks on the input must not flip open→closed
   * in the same gesture as focus (focus opens first). If the menu is closed but the input stayed focused
   * after a selection, focus does not refire — open explicitly on input click.
   */
  const handleControlBarClick = (e) => {
    if (disabled) return;
    const t = e.target;
    if (inputRef.current && (t === inputRef.current || inputRef.current.contains(t))) {
      if (!open) setOpen(true);
      return;
    }
    setOpen((o) => !o);
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <div
        className={`flex min-h-[42px] w-full items-center rounded-xl border border-dark-border bg-dark-surface transition-colors ${
          open ? 'ring-2 ring-vivid-red/50 border-vivid-red' : ''
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-text'}`}
        onClick={handleControlBarClick}
      >
        <Search className="w-4 h-4 flex-shrink-0 ml-3 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={open ? query : ''}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 min-w-0 bg-transparent border-0 py-2.5 px-3 text-sm text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-0"
          autoComplete="off"
          aria-label={comboboxAriaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={id ? `${id}-listbox` : undefined}
          role="combobox"
        />
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 mr-3 text-slate-400 transition-transform pointer-events-none ${
            open ? 'rotate-180' : ''
          }`}
        />
      </div>
      {menuRect &&
        typeof document !== 'undefined' &&
        createPortal(
          <DropdownSearchPortalLayer style={menuRect}>
            <AnimatePresence initial={false}>
              {open ? (
                <motion.ul
                  id={id ? `${id}-listbox` : undefined}
                  ref={listRef}
                  role="listbox"
                  className="overflow-y-auto overscroll-contain rounded-xl border border-dark-border bg-dark-surface shadow-lg py-1"
                  style={{ maxHeight: menuRect.maxHeight ?? 240 }}
                  onWheel={stopModalScrollLockPropagation}
                  onTouchMove={stopModalScrollLockPropagation}
                  initial={{ opacity: 0, scale: 0.95, y: -6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -6 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                >
                  {filtered.length === 0 ? (
                    <li className="px-4 py-3 text-sm text-slate-400">
                      {availableOptions.length === 0 ? 'All members added' : emptyMessage}
                    </li>
                  ) : (
                    filtered.map((option, i) => {
                      const isHighlighted = i === highlightIndex;
                      return (
                        <li
                          key={getOptionValue(option)}
                          role="option"
                          aria-selected={false}
                          data-highlighted={isHighlighted}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelect(option);
                          }}
                          onMouseEnter={() => setHighlightIndex(i)}
                          className={`cursor-pointer px-4 py-2.5 text-left text-sm transition-colors ${
                            isHighlighted
                              ? 'bg-vivid-red/20 text-vivid-red'
                              : 'text-slate-100 hover:bg-dark-elevated'
                          }`}
                        >
                          <span className="font-medium block truncate">{getOptionLabel(option)}</span>
                          {getOptionSublabel && getOptionSublabel(option) && (
                            <span className="text-xs text-slate-400 truncate block mt-0.5">
                              {getOptionSublabel(option)}
                            </span>
                          )}
                        </li>
                      );
                    })
                  )}
                </motion.ul>
              ) : null}
            </AnimatePresence>
          </DropdownSearchPortalLayer>,
          document.body
        )}
      {selectedOptions.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {selectedOptions.map((option) => {
            const value = getOptionValue(option);
            return (
              <span
                key={value}
                className="flex w-full min-w-0 items-center justify-between gap-2 pl-2.5 pr-1.5 py-1 rounded-lg bg-dark-elevated text-sm text-slate-200"
              >
                <span className="min-w-0 flex-1 truncate font-mono">{getOptionLabel(option)}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(value);
                  }}
                  className="p-0.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-900/20 transition-colors"
                  aria-label={`Remove ${getOptionLabel(option)}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdownSearch;
